import {
  DragHandle,
  DragSource,
  DropTarget,
} from '@wandb/weave/common/containers/DragDropContainer';
import {Button} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import produce from 'immer';
import _ from 'lodash';
import React, {memo, useCallback, useMemo, useRef, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import {trackPanelBankEvent} from '../../services/analytics';
import {useDispatch} from '../../state/hooks';
import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import * as ViewHooks from '../../state/views/hooks';
import * as PanelTypes from '../../state/views/panel/types';
import * as PanelBankSectionConfigActions from '../../state/views/panelBankSectionConfig/actions';
import {usePanelBankSectionConfig} from '../../state/views/panelBankSectionConfig/hooks';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {
  isDraggingWithinSection,
  isPanel,
  isPanelBankSection,
} from '../../util/panelbank';
import {PANEL_BANK_HIDDEN_SECTION_NAME} from '../../util/panelbankTypes';
import {useDetectChanges} from '../../util/react-help/detectReactPropChanges';
import {useRunSelectorResizingContextMaybe} from '../MultiRunWorkspace/RunSelectorResizingContext';
import {SectionSettings} from '../SettingControls/SectionSettings';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {useSectionSettingsContext} from '../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {usePanelBankSectionIsOpen} from './hooks/panelBankSectionIsOpenHooks';
import {PanelBankFlowSection} from './PanelBankFlowSection';
import {PanelBankGridSection} from './PanelBankGridSection';
import {PanelBankSectionOverflowMenu} from './PanelBankSectionOverflowMenu';
import {PanelBankSectionPagination} from './PanelBankSectionPagination';
import {PanelBankSectionTitle} from './PanelBankSectionTitle';
import {getPanelsToTrack, TimerEntry} from './PanelBankTiming';
import {usePanelBankWidthContext} from './PanelBankWidthContext';
import type {SectionMetadata} from './types';

type PanelBankSectionProps = SectionMetadata & {
  isProjectReadOnly?: boolean;
  // query: Query.Query;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  defaultMoveToSectionRef?: PanelBankSectionConfigTypes.Ref;
  pinPanelBankSection?: (sectionId: PanelBankSectionConfigTypes.Ref) => void;
  isPinned: boolean;
  showPinningBorder?: boolean;
  isFirstSection?: boolean;
  moveSectionBefore(
    moveRef: PanelBankSectionConfigTypes.Ref,
    beforeSectionRef: PanelBankSectionConfigTypes.Ref
  ): void;
  movePanel(
    panelRef: PanelTypes.Ref,
    fromSectionRef: PanelBankSectionConfigTypes.Ref,
    toSectionRef: PanelBankSectionConfigTypes.Ref,
    toIndex: number
  ): void;
};

export function usePanelBankSectionActions(
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref
) {
  const dispatch = useDispatch();
  const updateName = useCallback(
    (newName: string) => {
      dispatch(
        PanelBankSectionConfigActions.updateName(
          panelBankSectionConfigRef,
          newName
        )
      );
    },
    [dispatch, panelBankSectionConfigRef]
  );

  return useMemo(
    () => ({
      updateName,
    }),
    [updateName]
  );
}

function usePanelBankSection({
  panelBankSectionConfigRef,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
}) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);

  const debouncedContext = usePanelSearchDebouncedContext();

  const actions = usePanelBankSectionActions(panelBankSectionConfigRef);
  const sectionIsOpen = usePanelBankSectionIsOpen({
    panelBankSectionConfigRef,
  });
  const panelBankSectionConfig = usePanelBankSectionConfig(
    panelBankSectionConfigRef
  );
  return useMemo(
    () => ({
      isEditingName,
      setIsEditingName,
      isOverflowMenuOpen,
      setIsOverflowMenuOpen,
      ...debouncedContext,
      ...actions,
      ...sectionIsOpen,
      panelBankSectionConfig,
    }),
    [
      actions,
      debouncedContext,
      isEditingName,
      panelBankSectionConfig,
      sectionIsOpen,
      isOverflowMenuOpen,
      setIsOverflowMenuOpen,
    ]
  );
}

export const PanelBankSection = memo(function PanelBankSection(
  props: PanelBankSectionProps
) {
  const {
    panelBankSectionConfigRef,
    activePanelRefs,
    inactivePanelRefs,
    panelSearchResults,
    panelSearchMatchCount = 0,
    isSectionNameSearchMatch,
    moveSectionBefore,
    movePanel,
    isProjectReadOnly,
    defaultMoveToSectionRef,
    pinPanelBankSection,
    isPinned,
    showPinningBorder,
    isFirstSection,
  } = props;

  const fullWorkspaceRefsContext = useWorkspaceRefsContext();
  const {panelBankConfigRef} = fullWorkspaceRefsContext;

  const sectionComponentRef = useRef<HTMLDivElement>(null);

  const {
    panelBankSectionConfig,
    updateName,
    sectionIsOpen,
    toggleSectionIsOpen,
    shouldRenderSection,
    isSearching,
    isEditingName,
    setIsEditingName,
    isOverflowMenuOpen,
    setIsOverflowMenuOpen,
  } = usePanelBankSection({
    panelBankSectionConfigRef,
  });

  const {targetSectionRef: workspaceDrawerTarget, shouldHidePanelContents} =
    useWorkspaceDrawerContext();
  const isWorkspaceDrawerTarget = useMemo(
    () => _.isEqual(panelBankSectionConfigRef, workspaceDrawerTarget),
    [panelBankSectionConfigRef, workspaceDrawerTarget]
  );

  const runSelectorResizingContext = useRunSelectorResizingContextMaybe();

  const normalizedPanelBankSectionConfig = ViewHooks.usePart(
    panelBankSectionConfigRef
  );

  const {inheritedSettings} = useSectionSettingsContext();

  const activePanelCount = activePanelRefs.length;
  const totalPanelCount = panelBankSectionConfig.panels.length;

  const {panelBankWidth} = usePanelBankWidthContext();
  const isMobile = panelBankWidth <= 768; // tablet breakpoint from globals.less

  const panelBankSectionRefs =
    ViewHooks.usePart(panelBankConfigRef).sectionRefs;
  const isLastSection = _.isEqual(
    panelBankSectionConfigRef,
    panelBankSectionRefs[panelBankSectionRefs.length - 1]
  );
  const isHiddenPanelsSection =
    isLastSection &&
    panelBankSectionConfig.name === PANEL_BANK_HIDDEN_SECTION_NAME;

  const shouldUseGridSectionComponent = // Don't use grid section (custom layout) on mobile
    !isMobile &&
    // Don't use grid section if searching (otherwise you get weird blank spaces in the layout)
    !isSearching &&
    panelBankSectionConfig.type === 'grid';

  const onRemovePinnedSection = useCallback(
    (e: React.SyntheticEvent) => {
      pinPanelBankSection?.(panelBankSectionConfigRef);
      // this button always unpins a section
      trackPanelBankEvent({action: 'Unpin panel bank section'});
      e.stopPropagation();
    },
    [panelBankSectionConfigRef, pinPanelBankSection]
  );

  const sectionName = panelBankSectionConfig.name;

  const trackedPanels = useMemo(() => {
    return getPanelsToTrack(
      panelBankSectionConfig,
      normalizedPanelBankSectionConfig.panelRefs,
      isSearching ? panelSearchResults : undefined
    );
  }, [
    panelBankSectionConfig,
    normalizedPanelBankSectionConfig.panelRefs,
    isSearching,
    panelSearchResults,
  ]);
  // BEWARE AND BEWARE AND BEWARE:
  // THIS IS DEAD CODE. THE 'HAS FIRST SECTION LOADED' CODE IS ALL BROKEN RIGHT
  // NOW AND NEEDS TO BE FIXED. I AM LEAVING THIS FOR NOW BECAUSE IT'S MORE
  // DISRUPTIVE TO REMOVE IT --NH
  const [loadTimes, setLoadTimes] = useState<TimerEntry[]>(trackedPanels);
  const stopTimerCallback = useCallback(
    (name: string, data: Record<string, unknown> | undefined) => {
      if (data && data.duration != null) {
        setLoadTimes(prev =>
          produce(prev, draft => {
            const loadingPanel = draft.find(p => p.id === name);
            if (loadingPanel) {
              loadingPanel.duration = data.duration as number;
            }
          })
        );
      }
    },
    [setLoadTimes]
  );

  const shouldRenderContent =
    !shouldHidePanelContents && !runSelectorResizingContext?.isResizing;

  useDetectChanges('PanelBankSection', {
    defaultMoveToSectionRef,
    isEditingName,
    isFirstSection,
    isOverflowMenuOpen,
    isProjectReadOnly,
    isSearching,
    normalizedPanelBankSectionConfig,
    npbscPanelRefs: normalizedPanelBankSectionConfig.panelRefs,
    panelBankSectionConfig,
    panelSearchResults,
    pinPanelBankSection,
    sectionIsOpen,
    setIsEditingName,
    setIsOverflowMenuOpen,
    shouldHidePanelContents,
    shouldRenderSection,
    showPinningBorder,
    toggleSectionIsOpen,
    updateName,
    ...props,
    ...fullWorkspaceRefsContext,
  });

  return (
    <Tailwind>
      <div
        className={
          showPinningBorder || isWorkspaceDrawerTarget
            ? 'border-[5px] border-gold-300'
            : ''
        }
        data-test={`panel-bank-section-[${sectionName}]`}
        ref={sectionComponentRef}>
        <DropTarget
          isValidDropTarget={({dragRef, dragData}) => {
            return (
              isPanelBankSection(dragRef) ||
              (isPanel(dragRef) &&
                !isDraggingWithinSection(panelBankSectionConfigRef, dragData))
            );
          }}
          partRef={panelBankSectionConfigRef}
          getClassName={({dragRef, dropRef}) => {
            const elementClassNames = ['panel-bank__section'];
            if (!sectionIsOpen) {
              elementClassNames.push('collapsed');
            }
            if (isHiddenPanelsSection) {
              elementClassNames.push('panel-bank__section__hidden-panels');
            }
            if (dragRef != null && isPanel(dragRef)) {
              // always added when dragging a panel, even if the panel is not in this section
              elementClassNames.push('dragging-panel');
            }
            if (
              dropRef != null &&
              !_.isEqual(dragRef, dropRef) &&
              _.isEqual(panelBankSectionConfigRef, dropRef)
            ) {
              if (isPanelBankSection(dragRef)) {
                elementClassNames.push('active-section-drop-target');
              }
              if (
                isPanel(dragRef) &&
                (!sectionIsOpen || totalPanelCount === 0)
              ) {
                elementClassNames.push('active-box-drop-target');
              }
            }
            return elementClassNames.join(' ');
          }}
          onDrop={({dragRef, dragData}) => {
            // Drag panel onto section
            if (isPanel(dragRef) && dragData) {
              movePanel(
                dragRef,
                dragData.fromSectionRef,
                panelBankSectionConfigRef,
                0
              );
            }
            // Drag section onto section
            if (isPanelBankSection(dragRef)) {
              if (!_.isEqual(dragRef, panelBankSectionConfigRef)) {
                moveSectionBefore(dragRef, panelBankSectionConfigRef);
              }
            }
          }}>
          <DragSource partRef={panelBankSectionConfigRef}>
            <div
              className={twMerge('flex h-[48px] items-center', 'px-12 py-4')}>
              {!isHiddenPanelsSection && (
                <DragHandle partRef={panelBankSectionConfigRef}>
                  <Button
                    aria-label="Drag section"
                    className="cursor-grab active:cursor-grabbing"
                    icon="drag-grip"
                    size="small"
                    variant="ghost"
                  />
                </DragHandle>
              )}
              <div className={twMerge('panel-bank__section-title', 'w-full')}>
                <span
                  className="panel-bank__section-title-left"
                  onClick={e => {
                    // Important note: we do some css padding hacks to increase clickable area to toggle section right now.
                    // user should be able to click on icon or title to toggle section
                    toggleSectionIsOpen();
                    e.stopPropagation();
                  }}>
                  <Button
                    icon={sectionIsOpen ? 'chevron-down' : 'chevron-next'}
                    variant="ghost"
                    size="small"
                    className="mr-8"
                    aria-label={`${
                      sectionIsOpen
                        ? `Collapse ${sectionName} section`
                        : `Expand ${sectionName} section`
                    }`}
                  />
                  <PanelBankSectionTitle
                    sectionName={sectionName}
                    updateName={updateName}
                    isSectionNameSearchMatch={isSectionNameSearchMatch ?? false}
                    panelSearchMatchCount={panelSearchMatchCount}
                    activePanelCount={activePanelCount}
                    isSearching={isSearching}
                    isEditingName={isEditingName}
                    setIsEditingName={setIsEditingName}
                    onRemovePinnedSection={
                      isPinned ? onRemovePinnedSection : undefined
                    }
                  />
                </span>
                {!isHiddenPanelsSection && (
                  <>
                    <div
                      className="panel-bank__section-title-right"
                      style={sectionIsOpen ? {} : {visibility: 'hidden'}}>
                      <PanelBankSectionPagination
                        activePanelCount={activePanelCount}
                        isSectionNameSearchMatch={
                          isSectionNameSearchMatch ?? false
                        }
                        panelBankSectionConfigRef={panelBankSectionConfigRef}
                        panelSearchMatchCount={panelSearchMatchCount}
                        sectionLayoutType={panelBankSectionConfig.type}
                      />
                      <SectionSettings
                        isPinned={isPinned}
                        panelBankSectionConfigRef={panelBankSectionConfigRef}
                        sectionComponentRef={sectionComponentRef}
                        pinPanelBankSection={pinPanelBankSection}
                      />
                      <PanelBankSectionOverflowMenu
                        activePanelCount={activePanelCount}
                        isProjectReadOnly={isProjectReadOnly}
                        panelBankSectionConfigRef={panelBankSectionConfigRef}
                        sectionLayoutType={panelBankSectionConfig.type}
                        sectionComponentRef={sectionComponentRef}
                        setIsEditingName={setIsEditingName}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* We're using this wrapper with {display: none} because a) we don't want to render the section if it's never opened,
           and b) we don't want to re-mount an open section if we close and re-open it. */}
            <div style={sectionIsOpen ? {} : {display: 'none'}}>
              {shouldRenderSection ? (
                shouldUseGridSectionComponent ? (
                  <PanelBankGridSection
                    activePanelRefs={activePanelRefs}
                    inactivePanelRefs={inactivePanelRefs}
                    inheritedSettings={inheritedSettings}
                    // loadingState={{stopTimerCallback}}
                    isPinned={isPinned}
                    isSectionNameSearchMatch={isSectionNameSearchMatch}
                    movePanelBetweenSections={movePanel}
                    panelBankSectionConfigRef={panelBankSectionConfigRef}
                    panelBankWidth={panelBankWidth}
                    panelSearchMatchCount={panelSearchMatchCount}
                    panelSearchResults={panelSearchResults}
                    isProjectReadOnly={isProjectReadOnly}
                    // when toggling workspace drawer or resizing run selector,
                    // redrawing panels as they resize can be very perf intensive
                    // and cause jumpy/laggy behavior. to avoid this problem,
                    // hide panel contents while panel bank is resizing.
                    // redrawing panels as they resize can be very perf intensive
                    // when toggling workspace drawer or resizing run selector,
                    shouldRenderContent={shouldRenderContent}
                  />
                ) : (
                  <PanelBankFlowSection
                    activePanelRefs={activePanelRefs}
                    defaultMoveToSectionRef={defaultMoveToSectionRef}
                    inactivePanelRefs={inactivePanelRefs}
                    inheritedSettings={inheritedSettings}
                    isPinned={isPinned}
                    isSectionNameSearchMatch={isSectionNameSearchMatch}
                    movePanelBetweenSections={movePanel}
                    panelBankSectionConfigRef={panelBankSectionConfigRef}
                    panelBankWidth={panelBankWidth}
                    panelSearchMatchCount={panelSearchMatchCount}
                    panelSearchResults={panelSearchResults}
                    isProjectReadOnly={isProjectReadOnly}
                    // when toggling workspace drawer or resizing run selector,
                    // redrawing panels as they resize can be very perf intensive
                    // and cause jumpy/laggy behavior. to avoid this problem,
                    // hide panel contents while panel bank is resizing.
                    // redrawing panels as they resize can be very perf intensive
                    // when toggling workspace drawer or resizing run selector,
                    shouldRenderContent={shouldRenderContent}
                  />
                )
              ) : null}
            </div>
          </DragSource>
        </DropTarget>
        <div className="panel-bank__divider" />
      </div>
    </Tailwind>
  );
});
